import { useSelector } from 'react-redux';
import { call, put, select } from 'typed-redux-saga';

import type { Action } from '@/domains/core/applicationState/interfaces';
import { HYDRATE } from '@/domains/core/applicationState/store/hydrateAction';
import { fetchArticlePictures } from '@/domains/productDiscovery/ArticleReviews/articleReviews.api';
import type { ArticlePicturesState } from '@/domains/productDiscovery/ArticleReviews/ArticleReviews.model';
import { SpartacuxProductDiscoveryDomainState } from '@/domains/productDiscovery/productDiscovery.model';

export const GET_PICTURES = 'product/GET_PICTURES';

export const GET_PICTURES_SUCCESS = 'product/GET_PICTURES_SUCCESS';

export const GET_PICTURES_FAILURE = 'product/GET_PICTURES_FAILURE';

export const initialPicturesState: ArticlePicturesState = {
  fetching: false,
  fetched: false,
  articleId: null,
  pictures: [],
  error: null,
};

// SELECTORS
export const articlePicturesSelector = (
  state: SpartacuxProductDiscoveryDomainState<'b2c'>,
) =>
  state?.productDiscovery?.articleReviews?.articlePictures ||
  initialPicturesState;

export const useCarouselPictures = () => useSelector(articlePicturesSelector);

// REDUCER
export default function reducer(
  state: ArticlePicturesState = initialPicturesState,
  action: Action,
): ArticlePicturesState {
  switch (action.type) {
    case HYDRATE:
      return articlePicturesSelector(action.state);

    case GET_PICTURES:
      return {
        pictures: [],
        articleId: action.articleId,
        fetched: false,
        fetching: true,
        error: null,
      };

    case GET_PICTURES_SUCCESS:
      return {
        ...state,
        pictures: action.pictures,
        fetching: false,
        fetched: true,
      };

    case GET_PICTURES_FAILURE:
      return {
        pictures: [],
        fetching: false,
        fetched: true,
        articleId: null,
        error: action.error,
      };

    default:
      return state;
  }
}

// SAGA
interface actionWithArticleId {
  type: string;
  articleId: number;
}

export function* getPicturesSaga(action: actionWithArticleId) {
  try {
    const picturesState = yield* select(articlePicturesSelector);
    if (picturesState.fetched && picturesState.articleId === action.articleId)
      return null;

    yield put({
      type: GET_PICTURES,
      articleId: action.articleId,
    });

    const pictures = yield* call(fetchArticlePictures, action.articleId);

    yield put({
      type: GET_PICTURES_SUCCESS,
      pictures,
    });
  } catch (error) {
    yield put({
      type: GET_PICTURES_FAILURE,
      error: error.message,
    });
  }
}
